import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { navigateToUpgrade } from '../features/subscription/upgradeNav';
import './FeatureGate.css';

/**
 * @param {Object}  props
 * @param {string}  props.featureId       Feature to gate on
 * @param {Object=} props.requiredTier    Optional { tier, name } — routes the merchant
 *                                        directly to /upgrade/checkout for that tier
 *                                        instead of the plan picker.
 */
const FeatureGate = ({
  featureId,
  children,
  fallback,
  showUpgradePrompt = true,
  silent = false, // When true, returns null during loading/error states (useful for nav items)
  upgradeMessage = "This feature is not available for your subscription tier. Upgrade to unlock this feature.",
  requiredTier = null,
}) => {
  const navigate = useNavigate();
  const { hasAccess, loading, error } = useFeatureAccess(featureId);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [merchantRole, setMerchantRole] = useState('OWNER');

  useEffect(() => {
    // Fetch current user info — only for the team-member vs owner branch below.
    // (We no longer need currentTier here since the modal was removed; the
    // upgrade page fetches its own subscription state.)
    const fetchUserInfo = async () => {
      try {
        const response = await fetch('/api/merchants/me', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setIsTeamMember(data.user?.isTeamMember || false);
          setMerchantRole(data.user?.merchantRole || 'OWNER');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchUserInfo();
  }, []);

  // Show loading state - FeatureGate is meant to be used inside Shell, so no Shell wrapper
  // If silent mode is enabled (for nav items), return null instead of loading/error UI
  if (loading) {
    if (silent) return null;
    return (
      <div className="feature-gate-loading">
        <div className="loading-spinner"></div>
        <span>Loading...</span>
      </div>
    );
  }

  if (error) {
    if (silent) return null;
    return (
      <div className="feature-gate-error">
        <span>Error loading feature: {error}</span>
      </div>
    );
  }

  if (hasAccess) {
    return children;
  }

  // Silent mode: hide entirely when feature is unavailable (used for nav items)
  if (silent) {
    return null;
  }

  if (fallback) {
    return fallback;
  }

  // For team members (non-owners), show permission denied message instead of upgrade prompt
  // They can't upgrade - only the store owner can
  if (isTeamMember || (merchantRole && merchantRole.toUpperCase() !== 'OWNER')) {
    return (
      <div className="feature-gate-upgrade">
        <div className="upgrade-content">
          <div className="upgrade-icon">🚫</div>
          <div className="upgrade-message">
            You don't have access to this feature. Please contact your store owner to upgrade the subscription or adjust your permissions.
          </div>
        </div>
      </div>
    );
  }

  // For store owners, show upgrade prompt. Clicking now routes straight
  // to the upgrade page (or directly to /upgrade/checkout for the exact
  // required tier if the caller supplied one) instead of opening a
  // pricing modal — matches the rest of the app's route-based flow.
  if (showUpgradePrompt) {
    return (
      <div className="feature-gate-upgrade">
        <div className="upgrade-content">
          <div className="upgrade-icon">🔒</div>
          <div className="upgrade-message">{upgradeMessage}</div>
          <button
            className="upgrade-button"
            onClick={() => navigateToUpgrade(navigate, requiredTier)}
          >
            View Pricing & Upgrade
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default FeatureGate;

